<template #default>
    <div class="d-flex flex-column">
        <step-header :title="$t(`engage.select-template.title`)" button-id="popover-info" />
        <engage-popover
            :header="$t(`${currentStep.name}.title`)"
            :intro1="$t(`${currentStep.name}.intro1`)"
            placement="bottom"
            target="popover-info"
        />
        <search-bar class="ml-1" @searchValueChanged="searchValueChanged" />
        <b-modal
            v-if="currentStep.template"
            id="preview-modal"
            :title="$t(`engage.select-template.modal-title`)"
            centered
            header-close-variant="light"
            @hide="onPreviewHide"
            @show="updateData"
        >
            <template #modal-header="{ close }">
                <div class="d-flex flex-row align-items-center mx-auto">
                    <b-avatar
                        :text="resolveInitials($t(`engage.select-template.modal-title`))"
                        class="mx-1 preview-avatar"
                        size="md"
                        variant="info"
                    />
                    <h5 class="m-0 p-0">{{ $t(`engage.select-template.modal-title`) }}</h5>
                </div>
                <button aria-label="Close" class="close text-light" type="button" @click="close()">×</button>
            </template>
            <message-preview
                :attachments="attachments"
                :components="messageComponents"
                :date="date"
                :message="message"
            />
            <template #modal-footer="{ hide }">
                <div class="d-flex justify-content-center w-100">
                    <b-button size="md" variant="light" @click="hide()">
                        {{ $t(`engage.select-template.modal-close`) }}
                    </b-button>
                </div>
            </template>
        </b-modal>
        <b-table
            ref="refTable"
            :busy="isLoading"
            :empty-text="$t('paginator.no-matching-records-found')"
            :fields="tableColumns"
            :items="templates"
            :no-sort-reset="true"
            :sort-by.sync="sortBy"
            :sort-desc.sync="isSortDirDesc"
            class="mt-2 engage-font-md"
            no-select-on-click
            primary-key="id"
            responsive
            select-mode="single"
            selectable
            show-empty
            sticky-header="100%"
            @row-clicked="rowClicked"
        >
            <template #table-busy>
                <div class="d-flex justify-content-center my-1">
                    <b-spinner />
                </div>
            </template>
            <template #cell(title)="data">
                <div class="d-flex align-items-center">
                    <b-avatar
                        v-b-tooltip.hover.bottom="
                            $t(
                                data.item.mediaType
                                    ? `entities.templates.type-media.${data.item.mediaType}`
                                    : 'entities.templates.type-default'
                            )
                        "
                        class="d-flex justify-content-center align-items-center mr-1"
                        variant="light"
                    >
                        <font-awesome-icon
                            v-if="data.item.mediaType && resolveMediaTemplateIcon(data.item.mediaType)"
                            :icon="resolveMediaTemplateIcon(data.item.mediaType)"
                            class="text-secondary"
                        />
                        <font-awesome-icon v-else :icon="['fa', 'text']" class="text-secondary" />
                    </b-avatar>
                    <strong>{{ data.item.title }}</strong>
                </div>
            </template>
            <template #cell(content)="data">
                <p class="m-0">{{ data.item.content }}</p>
            </template>
            <template #cell(checkbox)="data">
                <b-form-radio
                    v-model.number="selected"
                    :value="data.item.id"
                    class="p-0 m-0"
                    name="template"
                    @change="rowClicked(data.item, data.index)"
                />
            </template>
            <!-- Column: Actions -->
            <template #cell(actions)="data">
                <div class="d-flex justify-content-end">
                    <b-button
                        v-b-modal.preview-modal
                        class="btn-icon mr-50"
                        variant="light"
                        @click="setPreview(data.item)"
                    >
                        <font-awesome-icon class="text-secondary" icon="eye" />
                    </b-button>
                    <b-button class="btn-icon" variant="light" @click="editTemplate(data.item.id)">
                        <font-awesome-icon class="text-secondary" icon="pen" />
                    </b-button>
                </div>
            </template>
        </b-table>

        <b-modal
            id="edit-modal"
            centered
            header-close-variant="light"
            no-stacking
            @hide="onEditCancled()"
            @ok="onEditFinished()"
        >
            <new-engage-message-edit-message @editFinished="emitEditFinished" @templateFilled="setTemplateFilled" />
            <template #modal-header="{ close }">
                <h3 class="m-0 p-0 mx-auto">{{ currentStep.template.title }}</h3>
                <button aria-label="Close" class="close text-light" type="button" @click="close()">×</button>
            </template>
            <template #modal-footer="{ ok, cancel }">
                <div class="d-flex w-100 justify-content-between align-items-end">
                    <div>
                        <template v-if="currentStep.template.mediaType">
                            <strong>{{
                                $t(`entities.templates.form-file-type-${currentStep.template.mediaType}`)
                            }}</strong>
                            <b-form-file
                                v-if="!currentStep.attachments || !currentStep.attachments.length"
                                :accept="allowedFileTypes"
                                :browse-text="$t(`form-file.browse-text`)"
                                :disabled="isUploadingAttachment"
                                :drop-placeholder="$t(`form-file.drop-placeholder`)"
                                :placeholder="$t(`form-file.placeholder`)"
                                class="mt-50"
                                @input="uploadAttachment($event)"
                            ></b-form-file>
                            <p
                                v-else
                                class="attachment-confirmed d-flex justify-content-center align-items-center mt-75 mb-50"
                            >
                                <font-awesome-icon class="mr-25" icon="check-circle" />
                                <span>{{ currentStep.attachments[0].originalName }}</span>
                                <font-awesome-icon
                                    class="remove ml-1 cursor-pointer"
                                    icon="circle-xmark"
                                    @click="setCampaignAttachments({ attachments: [] })"
                                />
                            </p>
                        </template>
                    </div>
                    <div>
                        <b-button
                            v-b-modal.preview-modal
                            :disabled="isUploadingAttachment"
                            class="btn-icon mx-50"
                            variant="light"
                            @click="previewRequested = true"
                        >
                            <font-awesome-icon class="text-secondary" icon="eye" />
                        </b-button>
                        <b-button
                            :disabled="isUploadingAttachment"
                            class="mr-50"
                            size="md"
                            variant="light"
                            @click="cancel()"
                        >
                            {{ $t(`engage.edit-message.cancel`) }}
                        </b-button>
                        <b-button
                            :disabled="!currentStep.isDone || isUploadingAttachment"
                            size="md"
                            variant="primary"
                            @click="ok()"
                        >
                            {{ $t(`engage.edit-message.create-message`) }}
                        </b-button>
                    </div>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
import { BAvatar, BButton, BFormFile, BFormRadio, BModal, BSpinner, BTable, VBTooltip } from "bootstrap-vue";
import engagePopover from "../../components/engagePopover.vue";
import stepHeader from "../components/stepHeader.vue";
import searchBar from "../components/searchBar.vue";
import messagePreview from "../components/messagePreview.vue";
import newEngageMessageEditMessage from "./newEngageMessageEditMessage.vue";
import useTemplates from "@/composables/useTemplates";
import i18n from "@/libs/i18n";
import { computed, ref, watch } from "@vue/composition-api";
import { formatDate } from "@/utils/luxon";
import { DateTime } from "luxon";
import { resolveInitials } from "@/utils/users";
import useEngageForm from "@/views/modules/engage/useEngageForm";
import { resolveMediaTemplateIcon } from "@/utils/templates";
import useFileUploads from "@/composables/useFileUploads";
import { useToast } from "vue-toastification/composition";
import toastificationContent from "@/layouts/components/toastification/toastificationContent";

export default {
    components: {
        BButton,
        BTable,
        BSpinner,
        BFormRadio,
        BFormFile,
        BModal,
        BAvatar,
        engagePopover,
        stepHeader,
        searchBar,
        messagePreview,
        newEngageMessageEditMessage,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        doEdit: {
            type: Boolean,
            required: true,
        },
    },
    emits: ["editFinished", "templateSelected"],
    data() {
        return {
            previewMessage: "",
            editFinished: false,
            previewRequested: false,
            date: "",
            message: "",
            messageComponents: null,
            attachments: null,
            templateFilled: false,
        };
    },
    watch: {
        selected(newValue, _) {
            if (newValue !== null && !this.doEdit) {
                this.$emit("templateSelected", {
                    template: this.templates.find((template) => template.id === newValue),
                });
            }
        },
        doEdit(newValue, _) {
            if (newValue) {
                this.$bvModal.show("edit-modal");
            } else {
                this.$bvModal.hide("edit-modal");
            }
        },
    },
    mounted() {
        if (this.doEdit) {
            this.$bvModal.show("edit-modal");
        }
    },
    methods: {
        setPreview(item) {
            this.previewMessage = item.content;
            this.messageComponents = item.components;
            this.attachments = null;
        },
        editTemplate(id) {
            this.selected = id;
            this.$emit("templateSelected", {
                template: this.templates.find((template) => template.id === id),
                proceed: true,
            });
        },
        searchValueChanged(value) {
            this.templateSearchString = value;
        },
        onEditFinished() {
            this.editFinished = true;
            this.$emit("editFinished", { proceed: true });
        },
        onEditCancled() {
            if (!this.editFinished && !this.previewRequested) {
                this.$emit("editFinished", { cancel: true });
            }
        },
        onPreviewHide() {
            this.previewRequested = false;
            if (this.doEdit) {
                this.$bvModal.show("edit-modal");
            }
        },
        updateData() {
            this.date = formatDate(DateTime.now(), DateTime.DATE_FULL);

            if (!this.doEdit) {
                this.message = this.previewMessage;
                return;
            }

            this.message = this.currentStep.template?.content ?? "";

            this.messageComponents = this.currentStep.template?.components;
            this.attachments = this.currentStep.attachments;
        },
        setTemplateFilled(isFilled) {
            this.templateFilled = isFilled;
        },
        emitEditFinished(data) {
            this.$emit("editFinished", data);
        },
    },
    setup(_, context) {
        const bvModal = context.root.$bvModal;

        const toast = useToast();

        const { fetchTemplates, templates, isLoading, templateSearchString } = useTemplates();
        const selected = ref(null);
        const refTable = ref(null);
        const sortBy = ref("title");
        const isSortDirDesc = ref(false);

        const { uploadFile, clearFiles, getAllowedFileTypes } = useFileUploads();

        const rowClicked = (item, index) => {
            selected.value = item.id;
            refTable.value.selectRow(index);
        };

        const { currentStep, selectedEngageToEdit, setCampaignAttachments } = useEngageForm(bvModal);
        // we set the channel for edit mode because is necessary for the template request
        if (selectedEngageToEdit.value) {
            currentStep.value.channelId = selectedEngageToEdit.value.channelId;
        }

        fetchTemplates({
            params: {
                join: "channels||id",
                fields: "title,content,components,mediaType",
                s: {
                    "channels.id": currentStep.value.channelId,
                },
                sort: `${sortBy.value},${isSortDirDesc.value ? "DESC" : "ASC"}`,
            },
        }).then(() => {
            // we do it after templates fetch because we need the templates
            if (selectedEngageToEdit.value) {
                currentStep.value.channelId = selectedEngageToEdit.value.channelId;
                currentStep.value.template = templates.value.find(
                    (elem) => elem.id === selectedEngageToEdit.value.templateId
                );
                currentStep.value.isDone = true;
            }
            if (currentStep.value.template) {
                selected.value = currentStep.value.template.id;
            }
        });

        const tableColumns = [
            { key: "checkbox", label: "", tdClass: "template-radio-button-column" },
            {
                key: "title",
                label: i18n.t("engage.lists.table-column-template-name"),
                sortable: true,
            },
            {
                key: "content",
                label: i18n.t("engage.lists.table-column-template-message"),
                tdClass: "text-secondary",
            },
            { key: "actions", label: "" },
        ];

        const allowedFileTypes = computed(() =>
            getAllowedFileTypes(currentStep.value.template.mediaType, { platform: "whatsapp" }).join(",")
        );

        // Handle attachments
        // It's called attachments here because it becomes an asset only if sent
        const isUploadingAttachment = ref(false);

        const uploadAttachment = (event) => {
            isUploadingAttachment.value = true;
            clearFiles();

            return uploadFile(event)
                .then((attachment) => {
                    setCampaignAttachments({ attachments: [attachment] });
                })
                .catch((error) => {
                    setCampaignAttachments({ attachments: [] });

                    toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("upload.failed"),
                            text: i18n.t(error.message),
                            icon: "times",
                            variant: "danger",
                        },
                    });
                })
                .finally(() => {
                    isUploadingAttachment.value = false;
                });
        };

        // We need to watch for changes, because any change will undo the selection
        // and /or our selected item might be somewhere else now
        watch([() => refTable?.value?.localItems, isLoading, sortBy, isSortDirDesc], (newValues, oldValues) => {
            if (isLoading.value || !refTable?.value?.localItems.length) {
                return;
            }

            // Bootstrap table doesn't re-sort its internal array. We do this for them so the index we find lines up
            if (newValues[2] !== oldValues[2] || newValues[3] !== oldValues[3]) {
                const newSortBy = newValues[2];
                const sortModifier = newValues[3] ? -1 : 1;
                refTable.value.localItems.sort((templateA, templateB) => {
                    if (typeof templateA[newSortBy] === "number" && typeof templateB[newSortBy] === "number") {
                        return (templateA[newSortBy] - templateB[newSortBy]) * sortModifier;
                    }
                    return `${templateA[newSortBy]}`.localeCompare(`${templateB[newSortBy]}`) * sortModifier;
                });
            }

            const selectedIndex = refTable.value.localItems.findIndex((item) => item.id === selected.value);
            refTable.value.selectRow(selectedIndex);
        });

        return {
            allowedFileTypes,
            currentStep,
            templates,
            tableColumns,
            templateSearchString,
            selected,
            refTable,
            sortBy,
            isSortDirDesc,
            isLoading,
            rowClicked,
            formatDate,
            resolveInitials,
            resolveMediaTemplateIcon,

            isUploadingAttachment,
            uploadAttachment,
            setCampaignAttachments,
        };
    },
};
</script>

<style lang="scss">
@import "~@/assets/scss/modules/engage/messagePreview.scss";
@import "~@/assets/scss/modules/engage/editTemplate.scss";
@import "~@/assets/scss/modules/engage/selectTemplate.scss";
</style>
